import { create } from "zustand";
import type { SizeLocale } from "../api/gql/graphql";
import { AppMode } from "../types/types";

/**
 * SessionStore contains state that is not expected to change during
 * the current session. For example variables received from query
 * params.
 * IsInitialized is set to true when everything is fetched and the
 * app is ready to be rendered.
 */

interface SessionStore {
  mode: AppMode;
  context?: string;
  token: string;
  productID?: string;
  productName?: string;
  brandName?: string;
  genderAge?: string;
  chartId?: string;
  callbackUrl: string;
  sizeLocale?: SizeLocale;
  storeFinderUrl?: string;
  session: string;
  isInitialized: boolean;
  deviceId?: string;
  widgetComponentType?: string;
  paperType?: string;
  skipOnboarding?: boolean;

  applyParams: (p: {
    mode: AppMode;
    context?: string;
    token?: string;
    productID?: string;
    brandName?: string;
    genderAge?: string;
    productName?: string;
    chartId?: string;
    callbackUrl?: string;
    session?: string;
    sizeLocale?: string;
    storeFinderUrl?: string;
    deviceId?: string;
    widgetComponentType?: string;
    paperType?: string;
    skipOnboarding?: boolean;
  }) => void;
  setMode: (m: AppMode) => void;
  setInitialized: () => void;
}

export const useSessionStore = create<SessionStore>()((set, get) => ({
  mode: AppMode.Recommendation,
  context: undefined,
  token: "",
  productID: "",
  productName: undefined,
  brandName: undefined,
  genderAge: undefined,
  chartId: undefined,
  callbackUrl: "",
  session: "",
  sizeLocale: undefined,
  storeFinderUrl: undefined,
  deviceId: undefined,
  widgetComponentType: undefined,
  paperType: undefined,
  skipOnboarding: false,
  isInitialized: false,
  applyParams: (by) => {
    if (get().mode !== by.mode) {
      set(() => ({ mode: by.mode }));
    }
    if (get().context !== by.context) {
      set(() => ({ context: by.context }));
    }
    if (by.token !== undefined && get().token !== by.token) {
      set(() => ({ token: by.token }));
    }
    if (get().productID !== by.productID) {
      set(() => ({ productID: by.productID }));
    }
    if (get().brandName !== by.brandName) {
      set(() => ({ brandName: by.brandName }));
    }
    if (get().genderAge !== by.genderAge) {
      set(() => ({ genderAge: by.genderAge }));
    }
    if (get().productName !== by.productName) {
      set(() => ({ productName: by.productName }));
    }
    if (get().chartId !== by.chartId) {
      set(() => ({ chartId: by.chartId }));
    }
    if (by.callbackUrl !== undefined && get().callbackUrl !== by.callbackUrl) {
      set(() => ({ callbackUrl: by.callbackUrl }));
    }
    if (by.session !== undefined && get().session !== by.session) {
      set(() => ({ session: by.session }));
    }
    if (get().sizeLocale !== by.sizeLocale) {
      const sl = by.sizeLocale?.toUpperCase();
      // Check that sizelocale is a valid value
      if (
        sl === "EU" ||
        sl === "UK" ||
        sl === "US" ||
        sl === "MONDOPOINT" ||
        sl === "US_WOMEN" ||
        sl === "US_MEN" ||
        sl === "AU"
      ) {
        set(() => ({ sizeLocale: sl as SizeLocale }));
      } else {
        set(() => ({ sizeLocale: undefined }));
      }
    }
    if (get().storeFinderUrl !== by.storeFinderUrl) {
      set(() => ({ storeFinderUrl: by.storeFinderUrl }));
    }
    if (get().deviceId !== by.deviceId) {
      set(() => ({ deviceId: by.deviceId }));
    }
    if (get().widgetComponentType !== by.widgetComponentType) {
      set(() => ({ widgetComponentType: by.widgetComponentType }));
    }
    if (get().paperType !== by.paperType) {
      set(() => ({ paperType: by.paperType }));
    }
    if (get().skipOnboarding !== by.skipOnboarding) {
      set(() => ({ skipOnboarding: by.skipOnboarding }));
    }
  },
  setMode: (by) => {
    set(() => ({ mode: by }));
  },
  setInitialized: () => {
    set(() => ({ isInitialized: true }));
  },
}));
